'use client';

/**
 * **The PMS participant-type section of the Contacts drawer's attribute path**
 * (VALE-158).
 *
 * ## What this closes
 *
 * The drawer had two paths and [CTBIND-5] only reached one of them.
 * `PmsContactTypeSelector` (the picker) and `PmsContactBindingRow` (the stored
 * type, its bind outcome, and **Unbind**) are both mounted inside
 * `RoleSection`, which lived on the `ContactFormCard` path — so with
 * `contact-attribute-drawer` on, **both** disappeared from edit mode. [ATTR-7]
 * has since removed that flag and the `ContactFormCard` edit path with it, so
 * this module is now the *only* place either control reaches a contact being
 * edited. Had it not landed first, the loss would have gone live silently for
 * every firm at once: nobody could have filed a contact on the PMS matter, or
 * unfiled one.
 *
 * That the *binding row* goes too is easy to miss and is why this module owns
 * both halves. `PmsContactTypeSelector` deliberately renders nothing in read
 * mode *because* the binding row shows the stored fact beside it — so porting
 * the picker alone would have produced a surface that could set a type and then
 * never tell you whether it filed.
 *
 * ## The shape, and why it is a section rather than a row
 *
 * A section of its own, headed by the provider's own mark and name, holding:
 *
 * 1. a one-column `rows` block — the single `Participant Type` select row, and
 * 2. a `component` block with `title: null` (which *continues* the section)
 *    carrying the support caveat and the binding row.
 *
 * **Block 1 is conditional on the firm having a catalogue** (VALE-160). A
 * provider whose roles are global constants offers nothing to select, so a
 * Smokeball section is block 2 alone — carrying the heading itself, and holding
 * one read-only binding row: provider, role, state, no unbind. See the gate on
 * {@link useContactPmsAttributeBlocks} for why that is two questions rather than
 * one.
 *
 * This uses block composition exactly as [INVDRAW-2] / VALE-73 built it, so it
 * needs **no widening of the editor union** — the alternative considered and
 * rejected was adding `note` / `mark` arms to `AttributeSpec`, which would
 * change the contract for all five surfaces that render this treatment
 * (`components/attributes/CLAUDE.md` §2) in what is otherwise a regression fix.
 *
 * The one thing it did need is {@link AttributeRowsBlock.titleAdornment}, an
 * additive slot for the mark in the section heading — see that member for why
 * widening `title` to a `ReactNode` was the wrong way to get it.
 *
 * Putting the row inside `Role in the estate` instead was also rejected, on a
 * measurement rather than a preference: the shared label column resolves to
 * **91px** on this 672px drawer and the provider-qualified label needs ~175px,
 * so it would clip to a different phrase. Heading the section with the provider
 * is what lets the row itself be the short, unqualified `Participant Type`.
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AttributeBlock } from '@/components/attributes';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK
} from '@/components/attributes';
import { cn } from '@/lib/utils';
import {
  pmsMatterContactBindingsQueryKey,
  usePmsContactTypeSelection,
  usePmsMatterContactBindings
} from '@/lib/services/integrations/pms/hooks';
import type { PmsContactBinding } from '@/lib/services/integrations/pms/types';
// Imported directly, not via the `pms` barrel — several suites mock that barrel with a partial
// factory, which resolves this registry to `undefined` at module-init time. See its own header.
import { getPmsProviderPresentationFor } from '@/lib/services/integrations/pms/provider-presentation';
import type { Contact } from '@/lib/services/contacts/types';
// Deep, not via `./contact-form` — that barrel exports the card and its sections, and the drawer
// itself already reaches past it the same way (`./contact-form/ContactFormCard`).
import { PmsContactBindingRow } from './contact-form/PmsContactBindingRow';
import {
  ProviderMark,
  PMS_CONTACT_TYPE_SUPPORT_NOTE,
  PMS_CONTACT_TYPE_EMPTY_CATALOGUE_NOTE
} from './contact-form/PmsContactTypeSelector';
import { PmsContactTypeRefreshButton } from './contact-form/PmsContactTypeRefreshButton';
import { buildPmsParticipantTypeField } from './contact-attribute-specs';

/**
 * The caveat and the binding outcome, as the section's continuation block.
 *
 * The note is bound to [CTBIND-5]'s own constant rather than re-typed: it is
 * load-bearing disclosure (the offered list *cannot* be filtered to the types
 * valid for the matter type, because `actiontypeparticipanttypes` is unreachable
 * on our app registration), so the two surfaces must not be able to drift into
 * saying different things about the same limitation.
 *
 * **The caveat is conditional; the binding row is not** (VALE-160). That note
 * describes a limitation *of the offered list*, so on a provider that offers no
 * list it is not a caveat, it is a non-sequitur — it would tell a Smokeball
 * reader their choices might be incomplete when they were never given any.
 */
function PmsParticipantTypeFooter({
  matterId,
  contactId,
  contactName,
  showTypeCaveat,
  showEmptyCatalogueNote
}: {
  matterId: number;
  contactId: number;
  contactName?: string;
  /** Whether a catalogue was offered above, and therefore whether its caveat applies. */
  showTypeCaveat: boolean;
  /**
   * Whether the provider publishes a catalogue that has never been read (VALE-587) — so there is no
   * select row above, but there *is* a remedy in the section heading. Mutually exclusive with
   * `showTypeCaveat` by construction: one describes a list that was offered, the other its absence.
   */
  showEmptyCatalogueNote: boolean;
}) {
  return (
    /*
      `gap-3` (10.2px), not `gap-2` (6.8px) — measured. The gap between the
      `Participant type` row and the caveat below it is **10.86px**, so at 6.8px
      the binding row sat visibly tighter to the caveat than the caveat does to
      the field, and the three read as two groups rather than one section.
    */
    <div className="flex flex-col gap-3">
      {showTypeCaveat && (
        <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK, 'm-0')}>
          {PMS_CONTACT_TYPE_SUPPORT_NOTE}
        </p>
      )}
      {showEmptyCatalogueNote && (
        <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK, 'm-0')}>
          {PMS_CONTACT_TYPE_EMPTY_CATALOGUE_NOTE}
        </p>
      )}
      {/*
        `showProvider={false}` — the section heading directly above already
        carries this provider's mark and name, and repeating them one line below
        is the same attribution twice on a surface whose whole argument is
        density. The row still renders nothing at all when the contact has no
        stored binding, so an unfiled contact sees only the caveat.
      */}
      <PmsContactBindingRow
        matterId={matterId}
        contactId={contactId}
        contactName={contactName}
        showProvider={false}
        hasTypeRowAbove={showTypeCaveat}
      />
    </div>
  );
}

export interface ContactPmsAttributeSectionOptions {
  contact: Contact;
  /**
   * `hasMatterId` from the drawer. A binding is per-`(contact, matter)`, so the
   * **global contacts page** — which renders this same drawer with no matter
   * behind it — gets no section at all. This is the host gate
   * `MATTER_SCOPED_GROUPS` already applies to the roles group, not a second
   * derivation of the backend's `isMatterDestinedForPms`.
   */
  hasMatterContext: boolean;
  /** The matter the contact is being edited on, when there is one. */
  matterId?: number;
  /**
   * **The contact does not exist yet** (VALE-632) — this section is being
   * composed on the buffered *create* form rather than on a saved record.
   *
   * Two things change, and both follow from there being no contact id to key a
   * binding on:
   *
   * · **The selection is read from the buffer, not from the bindings query.**
   *   On the edit path the stored selection deliberately comes from the
   *   matter-scoped bindings read (see the note on that query below), because
   *   the row and the outcome beneath it must never disagree about what the
   *   contact is *filed* under. Nothing is filed yet here, so the only truthful
   *   source is the buffer the footer will send — `contact.pmsContactTypeId`,
   *   which `CreateContactRequest` carries and the create call applies.
   * · **The outcome footer is omitted.** It reports a binding by `contactId`,
   *   and every id it could be given is a placeholder. This is the same
   *   `!!matterId && !!contactId` gate `RoleSection` already applies on the
   *   RHF add path, so the converted surface keeps that behaviour exactly.
   *
   * The *selector* still renders, which is the point: today's add form lets a
   * reader choose a participant type and submits it with the create, so
   * dropping it would be a regression rather than a simplification.
   */
  addMode?: boolean;
}

/**
 * The section's blocks, or an empty array when there is nothing to offer and
 * nothing to report.
 *
 * ## Two questions, not one (VALE-160)
 *
 * This gate used to be a single catalogue check — *"does the firm's active PMS
 * supply a catalogue?"* — which was right while only a provider **with** a
 * catalogue could ever produce a binding. Since VALE-160 a provider without one
 * produces `derived` bindings, and the check was conflating two things that had
 * merely always agreed:
 *
 * 1. **Is there a catalogue to pick from?** ({@link usePmsContactTypeSelection}'s
 *    `isAvailable`.) Gates the `Participant Type` select row and its caveat.
 *    Still false for Smokeball, so it still gets **no selector** — its roles are
 *    global constants and there is nothing to choose.
 * 2. **Is there a binding to report?** Gates the section's existence. A row that
 *    exists is a positive signal; the *absence* of a catalogue is not one.
 *
 * Keying (2) on the binding rather than on an empty catalogue matters, because
 * an empty list on its own says nothing: the API returns no types both for a
 * provider with no catalogue *and* for an integration that has never synced one.
 * The `syncedAt` alongside it is what separates them (`null` = never asked), and
 * {@link publishesContactTypeCatalogue} is the one place that reads the pair —
 * but a *binding* is still the better signal here, because it is positive
 * evidence rather than an inference. Reading "this must be Smokeball" out of an
 * empty list would be the frontend's version of the method-presence sniffing
 * `PmsProviderInterface.derivesContactBindingsFromRoles` exists to avoid.
 *
 * Returning blocks (rather than rendering) is what keeps the gate a *hook* while
 * the field list stays the pure function it is: `buildContactAttributeSpecs`
 * takes no hooks by design, so anything only a query can answer resolves in the
 * host and arrives as data — the same shape `showGeneralNotes` already uses.
 */
export function useContactPmsAttributeBlocks({
  contact,
  hasMatterContext,
  matterId,
  addMode = false
}: ContactPmsAttributeSectionOptions): {
  blocks: AttributeBlock<Partial<Contact>>[];
  notePmsSelection: (patch: Partial<Contact>) => void;
} {
  const { provider, contactTypes, isAvailable, publishesCatalogue } =
    usePmsContactTypeSelection();
  const queryClient = useQueryClient();

  /*
    The stored selection comes from the matter-scoped bindings read — the same
    source `PmsContactBindingRow` displays and the same one [CTBIND-5]'s picker
    seeds from, deliberately, so the row and the outcome beneath it can never
    disagree about what this contact is filed under. It is *not* read off
    `contact.pmsContactTypeId`.
  */
  const { data: bindings } = usePmsMatterContactBindings(
    provider ?? undefined,
    matterId,
    // Not on the create path: there is no contact id for a binding to be keyed
    // on, so the read has nothing to match and its result is never consulted.
    isAvailable && !!matterId && !addMode
  );

  /**
   * **Seed the just-saved selection into the bindings cache, then refetch.**
   *
   * Without this the row goes straight back to `Set participant type…` the
   * moment you choose something, which is what UX testing found. The row's
   * value is read from the *bindings* query, and nothing about a contact commit
   * invalidates it — `query-dependencies.ts` has no PMS entry, so the query
   * keeps serving the pre-selection answer (no row for this contact) and the
   * grid faithfully renders the placeholder for it.
   *
   * **Seeding the cache rather than holding a local `useState` is deliberate.**
   * It keeps one source of truth — this row and `PmsContactBindingRow` beneath
   * it read the same query, so they cannot disagree — and it means an
   * **Unbind** (which invalidates the same key) clears the displayed value for
   * free, where separate local state would resurrect the stale selection.
   *
   * **`isBound` is seeded `false`, not `true`, and that is the honest answer.**
   * The backend awaits `saveSelection` before responding, so a 200 proves the
   * *selection* is stored — it proves nothing about the ActionStep call, which
   * happens after. So the type displays immediately (what the user asked for)
   * while the outcome reads `Not yet filed` until the refetch brings the real
   * bind state back. Claiming a successful bind here would be a lie the row
   * would then have to retract.
   *
   * **A Vale role-field change needs the same treatment, for a provider that has
   * no catalogue at all (VALE-297).** `pmsContactTypeId` is how an ActionStep
   * selection reaches here; a Smokeball firm never carries that field — its
   * mapping is *derived* from `isLegalPersonalRepresentative` /
   * `isBeneficiary`, filed by the backend's fire-and-forget, ~10s-throttled
   * matter re-assert (`syncOrUpdateMatterToPms`). Before this, a role save on a
   * Smokeball contact invalidated nothing: the early return below required
   * `pmsContactTypeId`, which such a patch never carries, so the drawer and the
   * listing kept serving the mapping recorded *before* the change until an
   * unrelated refetch happened to land.
   *
   * There is no new value to seed here — unlike a selection, nobody chose a
   * type, and what the backend will resolve it to depends on every role flag
   * together, not just this one patch. So this flips the **existing** row (if
   * any) to `isBound: false` — the exact "still landing" state
   * {@link isPmsContactBindingInFlight} already recognises — which re-arms the
   * bindings query's own in-flight poll (`pmsContactBindingsPollInterval`)
   * rather than inventing a second polling mechanism. A contact with no row yet
   * (its first role change) gets a placeholder row for the same reason: the
   * poll condition is `bindings.some(isPmsContactBindingInFlight)`, which
   * matches nothing for a contact absent from the array at all.
   */
  const notePmsSelection = useCallback(
    (patch: Partial<Contact>) => {
      if (!provider || !matterId) return;

      const isSelection = !!patch.pmsContactTypeId;
      const isRoleChange =
        !isAvailable &&
        (patch.isLegalPersonalRepresentative !== undefined ||
          patch.isBeneficiary !== undefined);

      if (!isSelection && !isRoleChange) return;

      const key = pmsMatterContactBindingsQueryKey(provider, matterId);
      queryClient.setQueryData<PmsContactBinding[]>(key, (previous) => {
        const rest = (previous ?? []).filter(
          (entry) => entry.contactId !== contact.id
        );
        const existing = previous?.find(
          (entry) => entry.contactId === contact.id
        );

        if (isSelection) {
          return [
            ...rest,
            {
              contactId: contact.id,
              pmsContactTypeId: patch.pmsContactTypeId as string,
              pmsContactTypeLabel: patch.pmsContactTypeLabel ?? '',
              /*
                A user just picked this, so it is a selection by construction — and
                stating it matters because the seed is what the row renders until
                the refetch lands: omitted, the `as` cast leaves `origin`
                `undefined`, which is not `'derived'` and so happens to keep the
                Unbind affordance by accident rather than on purpose (VALE-160).
              */
              origin: 'selected',
              isBound: false,
              lastError: null
            } as PmsContactBinding
          ];
        }

        // Role change on a role-deriving provider: keep the existing label (if any) so the
        // row does not flash to a placeholder — only the outcome is "still landing".
        return [
          ...rest,
          {
            contactId: contact.id,
            pmsContactTypeId: existing?.pmsContactTypeId ?? '',
            pmsContactTypeLabel: existing?.pmsContactTypeLabel ?? '',
            origin: 'derived',
            isBound: false,
            lastError: null
          } as PmsContactBinding
        ];
      });

      void queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient, provider, matterId, contact.id, isAvailable]
  );

  const blocks = useMemo<AttributeBlock<Partial<Contact>>[]>(() => {
    if (!provider || !hasMatterContext || !matterId) return [];

    const presentation = getPmsProviderPresentationFor(provider);
    /*
      Nothing is filed for a contact that does not exist, so on the create path
      there is no binding to find and the buffer is the only source of the
      current choice — see `addMode`.
    */
    const binding = addMode
      ? undefined
      : bindings?.find((entry) => entry.contactId === contact.id);

    /*
      Nothing to pick, nothing filed, and nothing to refresh — so the provider's
      heading would sit above an empty section. Note this is the *only* place the
      questions recombine, and it is a pure "is there anything to say?" test:
      with a catalogue the section always renders (the selector is the thing to
      say), and without one it renders when a binding exists.

      **`publishesCatalogue` is the third arm, added by VALE-587.** A catalogue
      the provider publishes but nobody has read yet (`syncedAt === null`) has
      nothing to select and usually nothing bound — yet it is exactly the state
      the refresh in the heading remedies, so the section has to render to offer
      it. Smokeball is untouched: its catalogue is *synced* and empty, which
      `publishesContactTypeCatalogue` reads as "publishes none".
    */
    if (!isAvailable && !binding && !publishesCatalogue) return [];

    /*
      The section heading, and *whichever block comes first carries it* — a block
      with `title: null` continues the section above, so with no selector row
      above it the footer would render headingless and the provider would go
      unnamed on the one surface that has no other attribution.
    */
    const heading = {
      title: presentation.label,
      titleAdornment: (
        <ProviderMark
          logoUrl={presentation.logoUrl}
          label={presentation.label}
          size={14}
        />
      ),
      /*
        The refresh that re-reads the catalogue, for a provider that publishes one
        (VALE-587).

        **Section heading rather than beside the `Participant type` label**, and
        that is a constraint of this surface rather than a preference: affordances
        here belong to the value cell and are *derived from the editor kind*
        (`components/attributes/CLAUDE.md` §2), so a one-off per-field action
        button would be the call-site-remembers-a-prop antipattern that package
        exists to prevent. It also reads correctly at section level: the refresh
        acts on the firm's whole catalogue, not on this contact's row.

        **`titleAction`, not `titleAdornment`.** The leading slot is for a *mark*
        that qualifies the title, so putting the button there rendered it between
        the ActionStep mark and the word ActionStep — two marks ahead of a name.
      */
      titleAction: publishesCatalogue ? (
        <PmsContactTypeRefreshButton provider={provider} />
      ) : undefined
    };

    /*
      **No selector without a catalogue** — the whole of "no contact-type selector
      for Smokeball" (VALE-160). Its roles are global constants, so there is
      nothing to choose; a row offering an empty list, or a synthetic catalogue
      built to make the shapes match, would both be inventing a choice the
      provider does not have.
    */
    const typeRowBlock: AttributeBlock<Partial<Contact>>[] = !isAvailable
      ? []
      : [
          {
            kind: 'rows',
            ...heading,
            /*
          **Inherits the surface's two columns — deliberately not `columns: 1`.**
          One column is not "the same row, wider": {@link ATTRIBUTE_LABEL_WIDTH}
          resolves a 1-column block's label to `45cqw − 45px` against a
          2-column block's `22.5cqw − …`, i.e. roughly double. So this row's
          value would start far to the right of every other value on the
          drawer, and the shared label column — the thing that makes
          left-aligned values read as a column at all (§1.1) — would visibly
          break at exactly one row.

          An empty cell beside it is the smaller cost, and it is the cost every
          odd-length block on this surface already pays.
        */
            fields: [
              buildPmsParticipantTypeField({
                options: contactTypes.map((type) => ({
                  value: type.externalId,
                  label: type.name
                })),
                selectedTypeId: addMode
                  ? (contact.pmsContactTypeId ?? null)
                  : (binding?.pmsContactTypeId ?? null),
                selectedTypeLabel: addMode
                  ? (contact.pmsContactTypeLabel ?? null)
                  : (binding?.pmsContactTypeLabel ?? null)
              })
            ]
          }
        ];

    /*
      **The footer is rendered on the create path too, and that is deliberate**
      (VALE-632). It looked like it should not be — it reports a binding keyed by
      `contactId`, and a record being created has only a placeholder id — but
      `PmsContactBindingRow` runs its own bindings read and returns `null` when
      nothing matches, which for a contact that does not exist is always. So the
      binding half self-gates, and dropping the whole block would take the two
      *notes* with it, including the empty-catalogue note that explains the
      refresh in the heading (VALE-587). That refresh is reachable from today's
      RHF add form, so removing it here would have been a regression rather than
      a simplification.
    */
    return [
      ...typeRowBlock,
      {
        kind: 'component',
        /*
          `null` *continues* the section the selector row opened, so the caveat
          and the outcome sit under the provider's own heading. With no selector
          row this block is the section's first, so it carries the heading
          itself — which is the whole shape of a Smokeball section: a
          provider-headed block holding one read-only state row.
        */
        ...(typeRowBlock.length > 0 ? { title: null } : heading),
        component: (
          <PmsParticipantTypeFooter
            matterId={matterId}
            contactId={contact.id}
            contactName={
              contact.organisationName ||
              [contact.firstName, contact.lastName].filter(Boolean).join(' ')
            }
            showTypeCaveat={typeRowBlock.length > 0}
            showEmptyCatalogueNote={
              typeRowBlock.length === 0 && publishesCatalogue
            }
          />
        )
      }
    ];
  }, [
    isAvailable,
    publishesCatalogue,
    provider,
    hasMatterContext,
    matterId,
    bindings,
    contactTypes,
    addMode,
    contact.id,
    contact.pmsContactTypeId,
    contact.pmsContactTypeLabel,
    contact.organisationName,
    contact.firstName,
    contact.lastName
  ]);

  return { blocks, notePmsSelection };
}
